import fetch from 'isomorphic-fetch';
import { encode } from 'html-entities';
import { v4 as uuidv4 } from 'uuid';

import { redirectToUrl } from '@gumtree/ui-library/src/utils/browser-service';
import { getNewInstance as getNewG5SInstance } from '@gumtree/ui-library/src/utils/suggestions-service-g5s';
import {
    ISuggestionsService,
    KeywordSuggestionsResponse,
    KeywordSuggestionsHitV2,
} from '@gumtree/ui-library/src/utils/suggestions-service-interface';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import joinExperimentsToString from '@gumtree/shared/src/util/join-experiments-to-string';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { normalizeLocationType } from '@gumtree/shared/src/util/location-utils';
import {
    isSameLocationName,
    shouldPersistLocationSelection,
} from '@gumtree/shared/src/util/location-selection-persist';

import { RecentSearchesItem, save as saveSearches } from './recent-service/recent-searches-service';
import {
    save as saveLocations,
    updateLocationInUserPrefCookie,
    cachePreferredLocation,
} from './recent-service/recent-locations-service';
import { G5SParams } from './reducers/g5s-params';
import { getGrowthbookClientSide } from '../../growthbook/init-growthbook';

let internalSuggestionsService: ISuggestionsService | undefined;
let isSubmitSearchCalled = false; // Browser seems to have a bug that calls submitSearch() a second time after a click on search item. This ensures submitSearch() is only ever called once

export const getSuggestionsService = () => internalSuggestionsService;

export const setIsSubmitSearchCalled = (val) => (isSubmitSearchCalled = val);


export const setupSuggestionsService = (g5SParams: G5SParams) => {
    internalSuggestionsService = getNewG5SInstance(
        g5SParams.busId,
        g5SParams.timeStamp,
        g5SParams.sign
    );
};

export const fetchLocationSuggestions = (
    value: string
): Promise<{ name: string; type: string; id?: string }[]> =>
    fetch(`/ajax/location/prefix?input=${value}`)
        .then((response) => response.json())
        .then((response) => response.locationList as { name: string; type: string; id?: string }[]);

export type KeywordSuggestion = {
    name: string;
    highlight: string;
    category?: string;
    categoryDisplayName?: string;
    categoryId?: string;
    searchOptionsExactMatch?: boolean;
};

export const transformResponseToSuggestions = (
    results: KeywordSuggestionsResponse
): KeywordSuggestion[] => {
    const getHighlight = (suggestion: KeywordSuggestionsResponse[number]) => {
        return suggestion?._highlightResult?.completion?.matchedWords?.[0] || '';
    };

    const [first, ...rest] = results;
    const withoutCategory =
        getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SUG_WITHOUT_CATEGORY, 'A') ===
        'B';
    return [
        ...(first ? [{ name: first.completion, highlight: getHighlight(first) }] : []),
        ...(!withoutCategory && first?.categories
            ? first.categories.map(({ id, canonicalName, localizedNames }) => {
                if (shouldShowCategory(id)) {
                    return {
                        name: first.completion,
                        highlight: getHighlight(first),
                        category: canonicalName,
                        categoryDisplayName: localizedNames?.en_GB,
                        categoryId: id,
                    }
                }
                return {
                    name: first.completion,
                    highlight: getHighlight(first),
                }
            })
            : []),
        ...rest.map((suggestion) => {
            const data: KeywordSuggestion = {
                name: suggestion.completion,
                highlight: getHighlight(suggestion),
            };
            if (withoutCategory) {
                data.searchOptionsExactMatch = true;
                if (shouldShowCategory(suggestion.categories[0].id)) {
                    data.category = suggestion.categories[0].canonicalName;
                    data.categoryDisplayName = suggestion.categories[0].localizedNames?.en_GB;
                    data.categoryId = suggestion.categories[0].id;
                }
            }
            return data;
        }),
    ];
};

export const shouldShowCategory = (categoryId: string) => {
    return categoryId !== '1';
}

export const getV2ApiUrl = (): string => {
    return '/bff-api/search-sug';
};

export const extractHighlightWord = (highlightValue: string): string => {
    const match = /<em>(.*?)<\/em>/.exec(highlightValue ?? '');
    return match ? match[1] : '';
};

export const transformResponseToSuggestionsV2 = (
    hits: KeywordSuggestionsHitV2[]
): KeywordSuggestion[] => {
    const suggestions: KeywordSuggestion[] = [];

    for (const hit of hits) {
        const highlight = extractHighlightWord(hit.highlightValue);

        if (!hit.categoryPath || hit.categoryPath.length === 0) {
            suggestions.push({
                name: hit.completion,
                highlight,
            });
        } else {
            for (const categoryItem of hit.categoryPath) {
                suggestions.push({
                    name: hit.completion,
                    highlight,
                    category: categoryItem.canonicalName,
                    categoryDisplayName: categoryItem.name,
                });
            }
        }

        if (suggestions.length >= 10) {
            break;
        }
    }

    return suggestions.slice(0, 10);
};

export const fetchKeywordSuggestionsV2 = async (
    value: string,
    options?: {
        experiments?: string;
        isChips?: boolean;
        extend?: {
            busId?: string | number;
        };
    }
): Promise<KeywordSuggestion[]> => {
    const url = getV2ApiUrl();
    const reqId = uuidv4();
    const isServer = typeof window === 'undefined';
    const experiments = options?.experiments
        || joinExperimentsToString(getGrowthbookClientSide())
        || 'control';
    const extend = options?.extend?.busId
        ? { busId: Number(options.extend.busId) || options.extend.busId }
        : undefined;

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({
                completion: value,
                experiments,
                reqId,
                platform: 'web',
                isChips: options?.isChips ?? isServer,
                ...(extend && { extend }),
            }),
        });

        const data = await response.json();

        if (data.code !== 200 || !data.hits) {
            return [];
        }

        return transformResponseToSuggestionsV2(data.hits);
    } catch {
        return [];
    }
};

export const fetchKeywordSuggestions = async (value: string): Promise<KeywordSuggestion[]> => {
    const isSrp7B = getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SRP_7, 'A') === 'B';

    if (isSrp7B) {
        return fetchKeywordSuggestionsV2(value);
    }

    const suggestionsService = getSuggestionsService();

    return !suggestionsService
        ? Promise.resolve([])
        : suggestionsService
              .search(value)
              .then((results: KeywordSuggestionsResponse = []) =>
                  transformResponseToSuggestions(results)
              );
};

export type SearchSubmitSource = 'suggestion' | 'form';

export const submitSearch = ({
    category = 'all',
    categoryDisplayName,
    categoryId,
    keyword,
    location,
    locationDetails,
    currentLocationDetails,
    searchOptionsExactMatch,
    additionalParams = {},
    requireKeywordCorrection,
    keywordPopulatedBy,
    cookieDomain,
    submitSource = 'suggestion',
    pageLocation,
}: {
    category: string | undefined;
    categoryDisplayName: string | undefined;
    categoryId?: string;
    keyword: string;
    location: string;
    locationDetails?: { name: string; type?: string; id?: string };
    currentLocationDetails?: { name: string; type?: string; id?: string };
    searchOptionsExactMatch: boolean;
    additionalParams?: Record<string, string | boolean | number>;
    requireKeywordCorrection: boolean;
    keywordPopulatedBy: GA4.SubmitListingSearchEvent['search']['keywordPopulatedBy'];
    cookieDomain?: string;
    submitSource?: SearchSubmitSource;
    pageLocation?: string;
}) => {
    if (isSubmitSearchCalled) {
        return;
    }
    
    isSubmitSearchCalled = true;
    
    const queryParams: string[] = [];
    
    if (category) {
        // After clicking the dropdown, the "search_category" is not encoded. If the category name contains "&", it will cause a wrong redirection.
        queryParams.push(`search_category=${encodeURIComponent(category)}`);
    }

    const isSrp6B = getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SRP_6, 'A') === 'B';
    if (isSrp6B) {
        queryParams.push(`selected_category=${category || 'all'}`);
    }

    if (keyword) {
        queryParams.push(`q=${encodeURIComponent(keyword)}`);
        queryParams.push(`search_term_populated_by=${keywordPopulatedBy}`);
        
        if (requireKeywordCorrection) {
            queryParams.push(`keyword_correction=suggest`);
        }
        
        const recentSearch: RecentSearchesItem = {
            name: keyword,
        };
        
        if (category) {
            recentSearch.category = category;
        }
        
        if (categoryDisplayName) {
            recentSearch.categoryDisplayName = categoryDisplayName;
        }

        if (categoryId) {
            recentSearch.categoryId = categoryId;
        }

        if (searchOptionsExactMatch) {
            queryParams.push(`searchOptionsExactMatch=true`);
            recentSearch.searchOptionsExactMatch = true;
        }
        
        saveSearches(recentSearch);
    }

    if (location) {
        queryParams.push(`search_location=${encode(location)}`);
        const locationDetailsForTracking = locationDetails ?? currentLocationDetails;
        const hasMatchingLocationDetails = isSameLocationName(
            locationDetailsForTracking?.name,
            location
        );

        const shouldPersistLocation =
            submitSource === 'suggestion' ||
            shouldPersistLocationSelection(location, pageLocation);

        const isSrp4B = getGrowthbookClientSide()?.getFeatureValue(GrowthBookFeature.SRP_4, 'A') === 'B';
        if (isSrp4B && shouldPersistLocation) {
            trackGA4PreNav<GA4.SelectLocationClick>({
                event: 'select_location_click',
                locationName: location,
                ...(hasMatchingLocationDetails && {
                    locationType: normalizeLocationType(locationDetailsForTracking?.type?.toUpperCase()),
                    locationId: locationDetailsForTracking?.id,
                }),
            });
        }

        if (shouldPersistLocation) {
            // Save location with only name, type, and id (strip any extra fields)
            const cleanLocation = locationDetails
                ? {
                      name: locationDetails.name,
                      ...(locationDetails.type && { type: locationDetails.type }),
                      ...(locationDetails.id && { id: locationDetails.id }),
                  }
                : { name: location };
            saveLocations(cleanLocation);
        }

        updateLocationInUserPrefCookie(location, cookieDomain);
        cachePreferredLocation(location);
    }

    const paramsKeys = Object.keys(additionalParams);

    if (paramsKeys.length) {
        paramsKeys.forEach((param) => {
            queryParams.push(`${param}=${additionalParams[param]}`);
        });
    }

    const searchUrl = `/search?${queryParams.join('&')}`;

    redirectToUrl(searchUrl);
};
